import { useCallback, useContext } from "react";
import { useRemoveFinancialDocumentFromImporterMutation } from "@api/financialDocument.api";
import { ImporterContext } from "./importer.context";
import { ImporterDocumentRow } from "./types";

interface UseRemoveImporterDocumentOptions {
    onRemoved?: () => void;
}

export function useRemoveImporterDocument(
    documentRow: ImporterDocumentRow | undefined,
    options?: UseRemoveImporterDocumentOptions,
) {
    const onRemoved = options?.onRemoved;
    const {
        removeDocumentRow,
        upsertDocumentRow,
        setRefreshEnabled,
        documentRows,
        revalidateDocument,
    } = useContext(ImporterContext);

    const removeFinancialDocument =
        useRemoveFinancialDocumentFromImporterMutation();

    const removeDocument = useCallback(async () => {
        if (!documentRow?.financialDocument) {
            return;
        }

        removeDocumentRow(documentRow);
        setRefreshEnabled(false);
        try {
            await removeFinancialDocument.mutateAsync(
                documentRow.financialDocument,
            );
        } catch {
            upsertDocumentRow(documentRow);
            setRefreshEnabled(true);
            return;
        }
        setRefreshEnabled(true);

        const otherDocumentsRowForSameAccount = documentRows.filter(
            (d) =>
                d.id !== documentRow.id &&
                d.financialDocument?.financialAccountId != null &&
                d.financialDocument?.financialAccountId ===
                    documentRow.financialDocument?.financialAccountId &&
                !d.financialDocument?.transactionsImportStatus,
        );

        otherDocumentsRowForSameAccount.forEach((row) => {
            if (row.financialDocument) {
                revalidateDocument({
                    financialDocumentId: row.financialDocument.id,
                });
            }
        });

        onRemoved?.();
    }, [
        documentRow,
        removeDocumentRow,
        upsertDocumentRow,
        removeFinancialDocument,
        setRefreshEnabled,
        documentRows,
        revalidateDocument,
        onRemoved,
    ]);

    return {
        removeDocument,
        isRemoving: removeFinancialDocument.isPending,
    };
}
